import { useState, type FormEvent } from 'react';
import { dial, normaliseAu } from '../twilio/dial';
import { useDeviceStore } from '../store/device';

/**
 * Always-visible outbound dialer (FR-51). Sits on the Dashboard. AU-only;
 * accepts `+61…`, `61…`, `0X…` and normalises to E.164. The Call button
 * triggers dial() which uses the existing Voice SDK Device — same path as
 * the per-row Call buttons in Recent calls + History.
 */
export function DialerPane() {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [calling, setCalling] = useState(false);

  const ready = useDeviceStore(s => s.ready);
  const audioUnlocked = useDeviceStore(s => s.audioUnlocked);
  const activeCall = useDeviceStore(s => s.activeCall);

  const normalised = normaliseAu(value);
  const blocked = !ready || !audioUnlocked || !!activeCall;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (blocked || !normalised) return;
    setError(undefined);
    setCalling(true);
    try {
      await dial(normalised);
      setValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place call.');
    } finally {
      setCalling(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Dial out</h3>
        <span className="text-xs text-slate-400">Australian numbers</span>
      </div>
      <form onSubmit={onSubmit} className="flex items-stretch gap-2">
        <input
          type="tel"
          inputMode="tel"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="+61 4xx xxx xxx"
          disabled={blocked || calling}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={blocked || calling || !normalised}
          className="rounded-lg bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {calling ? 'Calling…' : 'Call'}
        </button>
      </form>
      {value && !normalised && !error && (
        <p className="mt-2 text-xs text-amber-700">Not a valid AU number. Use +61… or 0X… format.</p>
      )}
      {!ready && (
        <p className="mt-2 text-xs text-slate-500">Connecting to Twilio Voice…</p>
      )}
      {ready && !audioUnlocked && (
        <p className="mt-2 text-xs text-slate-500">Click "Enable audio" above first.</p>
      )}
      {activeCall && (
        <p className="mt-2 text-xs text-slate-500">Hang up your current call before placing a new one.</p>
      )}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
    </div>
  );
}
